/**
 * TypeScript types and interfaces for whitelabel configuration injection
 * Provides type safety for Vue's provide/inject pattern - Subtask 5.2
 */

import type { InjectionKey } from 'vue'
import type { WhitelabelConfig } from '../config/whitelabel'

// Vue injection key with proper typing
export const WhitelabelConfigInjectionKey: InjectionKey<WhitelabelConfig> = Symbol('whitelabelConfig')

// Extended interface for injection result with availability status
export interface WhitelabelInjectionResult {
  config: WhitelabelConfig
  isAvailable: boolean
  error?: Error
}

// Configuration provider interface for dependency injection
export interface WhitelabelProvider {
  provide(): void
  getConfig(): WhitelabelConfig | null
  isConfigured(): boolean
}

// Feature availability interface for type-safe feature checking
export interface FeatureAvailability {
  showLeaderboard: boolean
  allowUserSwitching: boolean
  allowLanguageSwitching: boolean
  showProfilePage: boolean
  enableNotifications: boolean
}

// Branding configuration interface for theme-related settings
export interface BrandingConfiguration {
  appName: string
  primaryColor: string
  secondaryColor?: string
  logoUrl: string
  faviconUrl: string
  hasCustomBranding: boolean
}

// Composable return types for better IntelliSense support
export interface UseWhitelabelConfigReturn extends WhitelabelInjectionResult {}

export interface UseWhitelabelAvailabilityReturn {
  isConfigured: boolean
  hasCustomBranding: boolean
}

export interface UseWhitelabelFeaturesReturn {
  features: FeatureAvailability
  isFeatureEnabled: (feature: keyof FeatureAvailability) => boolean
}

export interface UseWhitelabelBrandingReturn extends BrandingConfiguration {}

// Error types for better error handling
export class WhitelabelConfigurationError extends Error {
  constructor(message: string, public readonly cause?: Error) {
    super(message)
    this.name = 'WhitelabelConfigurationError'
  }
}

export class WhitelabelInjectionError extends Error {
  constructor(message: string, public readonly injectionKey?: symbol) {
    super(message)
    this.name = 'WhitelabelInjectionError'
  }
}

// Type guards for runtime validation
export function isWhitelabelConfig(value: unknown): value is WhitelabelConfig {
  return (
    typeof value === 'object' &&
    value !== null &&
    'appName' in value &&
    'primaryColor' in value &&
    'features' in value
  )
}

export function isFeatureAvailability(value: unknown): value is FeatureAvailability {
  return (
    typeof value === 'object' &&
    value !== null &&
    'showLeaderboard' in value &&
    'allowUserSwitching' in value &&
    'allowLanguageSwitching' in value &&
    'showProfilePage' in value &&
    'enableNotifications' in value
  )
}